import React from "react";
import { Card, Box, Typography, Grid, Stack } from "@mui/material";
import {
  ShieldCheck,
  Eye,
  Cpu,
  Palette,
  Sparkles,
  Dice5,
  Layers,
} from "lucide-react";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";

const PILLARS = [
  {
    icon: Layers,
    title: "Decentralized & Trustless",
    badge: "Core Architecture",
    iconBg: colors.blue[50],
    iconColor: colors.primary.main,
    description:
      "Blockchain enabled lottery platform possessing the power of decentralisation makes it possible to withdraw the lone authority, making it a trustless system for all the parties involved.",
  },
  {
    icon: Eye,
    title: "Complete Transparency",
    badge: "On-Chain Ledger",
    iconBg: colors.emerald[50],
    iconColor: colors.emerald[600],
    description:
      "Transparency in the platform helps users to take a look at all the records stored on blockchain such as number of participants, available numbers for the draw, genuine winners and historical data.",
  },
  {
    icon: Cpu,
    title: "System Automation",
    badge: "Smart Contracts",
    iconBg: colors.purple[50],
    iconColor: colors.purple[600],
    description:
      "Lottery platform involves automation to reduce human involvement, which tends to make the system more secure and fair.",
  },
  {
    icon: Palette,
    title: "Organizer & Creator Flexibility",
    badge: "Creator Studio",
    iconBg: colors.amber[50],
    iconColor: colors.amber[700],
    description:
      "Platforms inherit flexibility in allowing users not just to participate in lottery but even allows them to become an organizer for lottery to create and earn through ticket sale.",
  },
  {
    icon: Sparkles,
    title: "Elegant & Simple Interface",
    badge: "User Experience",
    iconBg: colors.cyan[50],
    iconColor: colors.cyan[700],
    description:
      "Elegant interface with minimal and simple design makes the platform adaptable to users of traditional lottery systems.",
  },
  {
    icon: Dice5,
    title: "Unbiased Random Draws",
    badge: "Provably Fair",
    iconBg: colors.red[50],
    iconColor: colors.error.main,
    description:
      "Using a random number generator to draw the winning number, without any tampering or human involvement helps to deliver unbiased and fair decisions.",
  },
];

const AboutPillars = () => {
  return (
    <Box sx={{ mb: 6 }}>
      {/* Section Header Card */}
      <Card
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: borderRadiusPx.xl,
          backgroundColor: colors.dashboard.cardBg,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          boxShadow: customShadows.cardFlat,
          mb: 3.5,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.5}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: borderRadiusPx.lg,
                bgcolor: colors.blue[50],
                color: colors.primary.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(37, 99, 235, 0.12)",
              }}
            >
              <ShieldCheck size={22} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: colors.slate[900],
                  fontSize: "1.25rem",
                }}
              >
                Core Ecosystem Pillars
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: colors.slate[500], fontSize: "0.875rem" }}
              >
                Built on the foundational strengths of decentralization,
                fairness, and automation
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Card>

      {/* Pillars Grid */}
      <Grid container spacing={3}>
        {PILLARS.map((pillar, idx) => {
          const IconComp = pillar.icon;
          return (
            <Grid item xs={12} sm={6} lg={4} key={idx}>
              <Card
                sx={{
                  p: 3,
                  borderRadius: borderRadiusPx.xl,
                  backgroundColor: colors.dashboard.cardBg,
                  border: `1px solid ${colors.dashboard.cardBorder}`,
                  boxShadow: customShadows.cardFlat,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: colors.primary.main,
                    boxShadow: customShadows.cardHover,
                    transform: "translateY(-3px)",
                  },
                }}
              >
                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: borderRadiusPx.lg,
                        bgcolor: pillar.iconBg,
                        color: pillar.iconColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconComp size={22} />
                    </Box>
                    <Box
                      sx={{
                        px: 1.25,
                        py: 0.35,
                        borderRadius: borderRadiusPx.full,
                        bgcolor: pillar.iconBg,
                        color: pillar.iconColor,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                      }}
                    >
                      {pillar.badge}
                    </Box>
                  </Stack>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      color: colors.slate[900],
                      mb: 1,
                      fontSize: "1.05rem",
                    }}
                  >
                    {pillar.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: colors.slate[600], lineHeight: 1.65 }}
                  >
                    {pillar.description}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default AboutPillars;
